import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryParam, NumberParam } from 'use-query-params';
import { Grid } from '@material-ui/core';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import {
  BaseSporCardProps,
  SupplierPurchaseOrder
} from '../supplier/Supplier.interface';
import { useFindPurchaseOrderByTask } from '../supplier/Supplier.hooks';
import PurchaseOrderSubmitCard from './cards/PurchaseOrderSubmitCard';
import PurchaseOrderForm from './PurchaseOrderForm';

const StyledStickyGrid = styled(Grid)`
  position: sticky;
  top: -16px;
  height: 10px;
  overflow: visible;
`;

const PurchaseCreatePage: React.FC = () => {
  const history = useHistory();
  const { taskId: paramTaskId = '' } = useParams<{ taskId?: string }>();
  const taskId: number = parseInt(paramTaskId) || 0;
  const [supplierTaskId] = useQueryParam('supplierTaskId', NumberParam);
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [spor, setSpor] = useState<SupplierPurchaseOrder | undefined>();
  const {
    data: foundSpor,
    error: findError,
    get: findSpor
  } = useFindPurchaseOrderByTask(taskId);

  const cardProps: BaseSporCardProps = {
    spor: spor!,
    onChange: (newSpor: Partial<SupplierPurchaseOrder>) => {
      spor && setSpor({ ...spor, ...newSpor });
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  useEffect(() => {
    findSpor();
  }, [findSpor]);

  useEffect(() => {
    foundSpor && setSpor(foundSpor);
  }, [foundSpor]);

  useEffect(() => {
    setError(findError);
  }, [findError]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Purchase Order Form</h2>
        <PageTemplateHeaderActions flex>
          {spor?.task && (
            <TaskStatusActions
              task={spor.task}
              taskEntity={spor}
              onChange={(task: Task) => {
                // redirect to detail page
                history.push(`/portal/spors/task/${task.id}`);
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {spor && <PurchaseOrderForm {...cardProps} />}
          </Grid>
          <StyledStickyGrid item xs={12} md={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {spor?.task && (
                  <PurchaseOrderSubmitCard
                    onCancel={() => {
                      history.push(
                        typeof supplierTaskId !== 'undefined'
                          ? `/portal/suppliers/task/${supplierTaskId}/purchase`
                          : '/portal/suppliers'
                      );
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </StyledStickyGrid>
        </Grid>
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Purchase Order details saved.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default PurchaseCreatePage;
